import { Component, AfterViewInit, OnDestroy, NgZone } from "@angular/core";
import { Router } from "@angular/router";

import {
	InsaMetadataService,
	InsaService,
	LocalizeService,
} from "../../insa-services";
import { KendoColumnDefinition, PortfolioModel } from "../../insa-models";
import { appStateProvider } from "../../insa-providers";
import { appConfig } from "../../../insaweb.config";
import {
	customGroupHeader,
	SharedMethodsModule,
	CustomGridFilterModule,
} from "../../insa-custom-scripts";

@Component({
	template: `
		<!-- Detalji (Editor) -->
		<generic-editor
			*ngIf="isDetailsVisible"
			[metadataName]="'SecurityAdministration'"
			[selectedId]="selectedSecNumber"
			[canDelete]="false"
			[canEdit]="false"
			[onBack]="onDetailsBack"
		></generic-editor>

		<!-- Kontejner za Grid - Uvijek prisutan u DOM-u da bi Kendo loader radio -->
		<div [hidden]="isDetailsVisible" class="ptf-container">
			<div class="ptf gridResize">
				<!-- Na ovaj ID/klasu kendo.ui.progress lijepi spinner -->
				<div
					id="PortfolioGrid"
					(window:resize)="onResize($event)"
					class="portfGrid-loading"
				></div>
			</div>
		</div>

		<!-- Grid Wizard -->
		<grid-wizard
			[isVisible]="isGridWizardVisible"
			[gridName]="gridName"
			[metadataName]="metadataName"
			(onCloseModal)="closeGridWizard()"
			(onApplyGridSettings)="applyGridSettings($event)"
		></grid-wizard>
	`,
	styles: [
		`
			:host {
				display: flex;
				flex-direction: column;
				flex-grow: 1;
				position: relative; /* Važno za apsolutno pozicioniranje loadera unutar hosta */
			}
			.ptf-container {
				flex-grow: 1;
				display: flex;
				flex-direction: column;
			}
			.ptf {
				flex-grow: 1;
				height: 100%;
			}
			/* Osigurava da Kendo loader prekrije cijeli grid prostor */
			.portfGrid-loading {
				height: 100%;
				min-height: 200px;
			}
		`,
	],
})
export class PortfolioComponent implements AfterViewInit, OnDestroy {
	private gridName = "PortfolioGrid";
	private metadataName = "Portfolio";
	private portfolioSubscription;
	isDetailsVisible: boolean = false;
	selectedSecNumber;
	onDetailsBack: () => void;
	enableLockedColumnOnPortfolioGrid: boolean = false;
	private customIconGraph = appConfig.customIconGraph;
	isGridWizardVisible: boolean = false;
	isLoading: boolean = true;
	private isApplyingGridSettings: boolean = false;

	constructor(
		private _insaService: InsaService,
		private router: Router,
		private _appStateProvider: appStateProvider,
		private _localize: LocalizeService,
		private _insaMetadataService: InsaMetadataService,
		private _zone: NgZone,
	) {
		this.onDetailsBack = this.toggleDetails.bind(this);
		this.openGridWizard = this.openGridWizard.bind(this);
		this.portfolioSubscription = this._insaService.PortfolioEvent.subscribe(
			(portf) => {
				console.log(portf);
				kendo.ui.progress($(".portfGrid-loading"), true);
				setTimeout(() => {
					this.refreshPortfolios(portf);
				}, 100);
			},
		);
	}

	toggleDetails() {
		this.isDetailsVisible = !this.isDetailsVisible;
	}

	private refreshPortfolios(portfolios: PortfolioModel[]) {
		this.InitKendoGridPortfolios(
			this.createPortfolioGridColumnDef(),
			portfolios || [],
		);

		this.hideColumnsDependingOnScreenSize(window.innerWidth);

		this._zone.run(() => {
			kendo.ui.progress($(".portfGrid-loading"), false);
		});
	}

	private createPortfolioGridColumnDef() {
		let portfolioGridColumnDef = new Array<KendoColumnDefinition>();
		let aggregateTemplate: string =
			"<div style='float: right'>#= (sum!==null) ? kendo.toString(sum, 'n2') : '' #</div>";
		let aggregateTemplatePercent: string =
			"<div style='float: right'>#=(sum!==null) ? kendo.toString(sum, 'n2') + '%' : ''#</div>";
		let aggregateTemplatePercentMultiply: string =
			"<div style='float: right'>#=(sum!==null) ? kendo.toString(sum * 100, 'n2') + '%' : ''#</div>";

		portfolioGridColumnDef = new Array<KendoColumnDefinition>();

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"SecName",
				this._localize.getLocalizedResource("Res3516"),
				'#:(SecName!==null) ? SecName : "" #',
				"",
				true,
			),
		);
		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"SecISIN",
				this._localize.getLocalizedResource("Res960"),
				'#:(SecISIN!==null) ? SecISIN : "" #',
				"",
				true,
			),
		);
		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"SecNumber",
				this._localize.getLocalizedResource("Res3751"),
				'#:(SecNumber!==null) ? SecNumber : "" #',
				"",
				true,
			),
		);
		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"Maturity",
				this._localize.getLocalizedResource("Res3753"),
				'#: (Maturity!==null)? kendo.toString(Maturity, "dd.MM.yyyy"): ""#',
				"{0:dd.MM.yyyy}",
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"Quantity",
				this._localize.getLocalizedResource("Res3755"),
				'#: (Quantity!==null) ? kendo.toString(Quantity, "n2"): "" #',
				"",
				false,
				false,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"Currency",
				this._localize.getLocalizedResource("Res724"),
				'#:(Currency!==null) ? Currency : "" #',
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"CurrentPrice",
				this._localize.getLocalizedResource("Res3671") +
					" (" +
					this._localize.getLocalizedResource("Res434") +
					")",
				'#: (CurrentPrice!==null) ? kendo.toString(CurrentPrice, "n2"): "" #',
				"{0:#.##}",
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"PriceDate",
				this._localize.getLocalizedResource("Res896"),
				'#:(PriceDate!==null) ? kendo.toString(PriceDate, "dd.MM.yyyy") : "" #',
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"Valuation",
				this._localize.getLocalizedResource("Res3756") +
					" (" +
					this._localize.getLocalizedResource("Res435") +
					")",
				'#: (Valuation!==null) ? kendo.toString(Valuation, "n2"): "" #',
				"{0:#.##}",
				false,
				false,
				["sum"],
				"<div style='float: right'>#= (sum!==null) ? kendo.toString(sum, 'n2') : '' #</div>",
				"<div style='float: right'>#= (sum!==null) ? kendo.toString(sum, 'n2') : ''#</div>",
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"PurcPrice",
				this._localize.getLocalizedResource("Res659") +
					" (" +
					this._localize.getLocalizedResource("Res434") +
					")",
				'#: (PurcPrice!==null) ? kendo.toString(PurcPrice, "n2"): "" #',
				"{0:#.##}",
				false,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"PurchaseValue",
				this._localize.getLocalizedResource("Res660") +
					" (" +
					this._localize.getLocalizedResource("Res435") +
					")",
				'#: (PurchaseValue!==null) ? kendo.toString(PurchaseValue, "n2"): "" #',
				"{0:#.##}",
				false,
				false,
				["sum"],
				aggregateTemplate,
				aggregateTemplate,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"UnrealisedProfit",
				this._localize.getLocalizedResource("Res2249") +
					" (" +
					this._localize.getLocalizedResource("Res435") +
					")",
				'#: (UnrealisedProfit!==null) ? kendo.toString(UnrealisedProfit, "n2"): "" #',
				"{0:#.##}",
				false,
				false,
				["sum"],
				aggregateTemplate,
				aggregateTemplate,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"ProfitPerc",
				this._localize.getLocalizedResource("Res2249") + " (%)",
				'#: (ProfitPerc!==null) ? kendo.toString(ProfitPerc * 100, "n2") + "%": "" #',
				"{0:#.##}",
				false,
				false,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"AccruedInterest",
				this._localize.getLocalizedResource("Res712") +
					" (" +
					this._localize.getLocalizedResource("Res435") +
					")",
				'#: (AccruedInterest!==null) ? kendo.toString(AccruedInterest, "n2"): "" #',
				"{0:#.##}",
				false,
				false,
				["sum"],
				aggregateTemplate,
				aggregateTemplate,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"PercentOfTotal",
				this._localize.getLocalizedResource("Res225"),
				'#: (PercentOfTotal!==null) ? kendo.toString(PercentOfTotal, "n2") + "%": "" #',
				"{0:#.##}",
				false,
				false,
				["sum"],
				aggregateTemplatePercent,
				aggregateTemplatePercent,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"Domicile",
				this._localize.getLocalizedResource("Res2118"),
				'#:(Domicile!==undefined && Domicile!==null) ? Domicile : "" #',
				"",
				true,
				true,
				[],
				"",
				"",
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"Yield",
				this._localize.getLocalizedResource("Res3769"),
				'#:(Yield!==undefined && Yield!==null) ? kendo.toString(Yield * 100, "n2") + "%" : "" #',
				"",
				false,
				false,
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"PurchaseDay",
				this._localize.getLocalizedResource("Res940"),
				'#:(PurchaseDay!==undefined && PurchaseDay!==null) ? kendo.toString(PurchaseDay, "dd.MM.yyyy") : "" #',
				"",
				true,
				true,
				[],
				"",
				"",
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"RatingSnP",
				this._localize.getLocalizedResource("Res380"),
				'#:(RatingSnP!==undefined && RatingSnP!==null) ? RatingSnP : "" #',
				"",
				true,
				true,
				[],
				"",
				"",
			),
		);

		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"InterestFrequency",
				this._localize.getLocalizedResource("Res397"),
				'#:(InterestFrequency!==undefined && InterestFrequency!==null) ? InterestFrequency : "" #',
				"",
				true,
				true,
				[],
				"",
				"",
			),
		);
		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"ClientNumber",
				this._localize.getLocalizedResource("Res3633"),
				'#:(ClientNumber!==null) ? ClientNumber : "" #',
				"",
				true,
			),
		);
		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"Bank",
				this._localize.getLocalizedResource("Res3238"),
				'#:(Bank!==null) ? Bank : "" #',
				"",
				true,
			),
		);
		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"MainClass",
				this._localize.getLocalizedResource("Res904"),
				'#:(MainClass!==null) ? MainClass : "" #',
			),
		);
		portfolioGridColumnDef.push(
			new KendoColumnDefinition(
				"SubClass",
				this._localize.getLocalizedResource("Res908"),
				'#:(SubClass!==null) ? SubClass : "" #',
				"",
				true,
			),
		);

		this.setPropertiesForPortfolioGrid(portfolioGridColumnDef);
		this.setGroupHeaderTemplate(portfolioGridColumnDef);

		return portfolioGridColumnDef;
	}

	private exportToExcel() {
		let portfGrid: any = $("#" + this.gridName);
		if (portfGrid !== undefined) {
			portfGrid.getKendoGrid().saveAsExcel();
		}
	}

	private setPropertiesForPortfolioGrid(kendoColumn) {
		let columnStyleLeftAlign: any = {
			style: "font-size:1rem;text-transform:none;",
		};
		let headerStyleLeftAlign: any = { style: "text-transform:none;" };
		let columnStyleRightAlign: any = {
			style: "text-align:right;font-size:1rem;text-transform:none;",
		};
		let headerStyleRightAlign: any = {
			style: "text-align:right;text-transform:none;",
		};

		for (let i = 0; i < kendoColumn.length; i++) {
			switch (kendoColumn[i].field) {
				case "MainClass":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					kendoColumn[i].sortable = {
						compare:
							SharedMethodsModule.makeCustomCompareFunction("SecMainClass"),
					};
					kendoColumn[i].groupable = {
						sort: {
							compare:
								SharedMethodsModule.makeCustomCompareFunction("SecMainClass"),
						},
					};
					break;

				case "SecNumber":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					break;
				case "SecISIN":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					break;
				case "Maturity":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					break;
				case "PriceDate":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					break;
				case "Currency":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					break;
				case "SecName":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					if (this.enableLockedColumnOnPortfolioGrid) {
						kendoColumn[i].locked = true;
					} else {
						kendoColumn[i].locked = false;
					}
					break;
				case "Quantity":
					kendoColumn[i].attributes = headerStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					kendoColumn[i].groupable = false;
					break;
				case "CurrentPrice":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "Valuation":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "PurcPrice":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "PurchaseValue":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "UnrealisedProfit":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "ProfitPerc":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "AccruedInterest":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "PercentOfTotal":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;
				case "Domicile":
					kendoColumn[i].attributes = columnStyleLeftAlign;
					kendoColumn[i].headerAttributes = headerStyleLeftAlign;
					break;
				case "Yield":
					kendoColumn[i].attributes = columnStyleRightAlign;
					kendoColumn[i].headerAttributes = headerStyleRightAlign;
					kendoColumn[i].groupable = false;
					break;

				default:
					console.log("");
			}
		}

		SharedMethodsModule.fitColumnsToHeaderTitles(kendoColumn);
	}

	private setGroupHeaderTemplate(arr: Array<KendoColumnDefinition>) {
		arr.forEach((element) => {
			element.groupHeaderTemplate =
				"#= getGroupInfo(aggregates,data, getKendoGrid('PortfolioGrid')) #";
		});
	}

	private goToSecurityPrice(secNumber: string) {
		if (secNumber !== undefined) {
			if (this.customIconGraph.enabled) {
				this.router.navigate([this.customIconGraph.pageRedirect], {
					queryParams: { secNumber: secNumber },
				});
			} else {
				this._appStateProvider.fetchingSecurityPrices = true;
				this.router.navigate(["/security"]);
				this._insaService.getSecPricesChart(secNumber);
			}
		} else {
			if (this.customIconGraph.enabled) {
				this.router.navigate([this.customIconGraph.pageRedirect]);
			} else {
				this.router.navigate(["/security"]);
				kendo.ui.progress($(".stockChart-loading"), false);
			}
		}
	}

	private openDetails(secNumber: string) {
		if (secNumber !== undefined) {
			this.selectedSecNumber = secNumber;
			this.toggleDetails();
		}
	}

	private hideColumnsDependingOnScreenSize(gridWidth) {
		let kendoPortfGrid: kendo.ui.Grid = $("#" + this.gridName).data(
			"kendoGrid",
		);
		if (!kendoPortfGrid) return;
		let colVisibilityChanged: boolean = false;

		// Show all columns on both desktop and mobile;
		// only client-specific hidden columns remain hidden.
		kendoPortfGrid.columns.forEach((col) => {
			if (!appConfig.clientSpecificColumnHidden.includes(col.field)) {
				if (col.hidden) colVisibilityChanged = true;
				kendoPortfGrid.showColumn(col);
			} else {
				kendoPortfGrid.hideColumn(col);
			}
		});

		if (colVisibilityChanged) {
			kendoPortfGrid.refresh();
			$("#" + this.gridName)
				.find(".k-icon.k-i-collapse")
				.trigger("click");
			colVisibilityChanged = false;
		}
	}

	private onResize(event) {
		let portfGrid: kendo.ui.Grid = $("#" + this.gridName).data("kendoGrid");
		this.hideColumnsDependingOnScreenSize(event.target.innerWidth);
		if (portfGrid !== undefined) {
			portfGrid.resize(true);
		}
	}

	private InitKendoGridPortfolios(columnDefs, griddata) {
		let dataSourceOptions: kendo.data.DataSourceOptions = {};
		dataSourceOptions.data = griddata;
		dataSourceOptions.group = [
			{
				field: "MainClass",
				compare: SharedMethodsModule.makeCustomCompareFunction("SecMainClass"),
				aggregates: [
					{ field: "Valuation", aggregate: "sum" },
					{ field: "Quantity", aggregate: "sum" },
					{ field: "CurrentPrice", aggregate: "sum" },
					//{field:"Yield",aggregate: "sum"},
					{ field: "AccruedInterest", aggregate: "sum" },
					{ field: "PurchaseValue", aggregate: "sum" },
					{ field: "PercentOfTotal", aggregate: "sum" },
					{ field: "UnrealisedProfit", aggregate: "sum" },
				],
			} as kendo.data.DataSourceGroupItem,
		];
		dataSourceOptions.aggregate = [
			{ field: "Valuation", aggregate: "sum" },
			{ field: "PurchaseValue", aggregate: "sum" },
			{ field: "Quantity", aggregate: "sum" },
			{ field: "CurrentPrice", aggregate: "sum" },
			{ field: "PercentOfTotal", aggregate: "sum" },
			{ field: "AccruedInterest", aggregate: "sum" },
			{ field: "UnrealisedProfit", aggregate: "sum" },
			//{field:"Yield",aggregate: "sum"}
		];
		dataSourceOptions.schema = {
			model: {
				fields: {
					SecName: { type: "string" },
					SecNumber: { type: "string" },
					SecISIN: { type: "string" },
					Maturity: { type: "date" },
					Quantity: { type: "number" },
					Currency: { type: "string" },
					CurrentPrice: { type: "number" },
					Valuation: { type: "number" },
					PriceDate: { type: "date" },
					PurcPrice: { type: "number" },
					PurchaseValue: { type: "number" },
					UnrealisedProfit: { type: "number" },
					ProfitPerc: { type: "number" },
					AccruedInterest: { type: "number" },
					PercentOfTotal: { type: "number" },
					MainClass: { type: "string" },
					Domicile: { type: "string" },
					Yield: { type: "number" },
					RatingSnP: { type: "string" },
					PurchaseDay: { type: "date" },
					ClientNumber: { type: "string" },
					Bank: { type: "string" },
					SubClass: { type: "string" },
					// InterestFrequency: {type: "number"}
				},
			},
		};

		let dataSource: kendo.data.DataSource = new kendo.data.DataSource(
			dataSourceOptions,
		);

		columnDefs.splice(
			1,
			0,
			{
				width: 55,
				command: [
					{
						name: "goToSecurityCommand",
						text: appConfig.useLegacyIcons
							? '<img src="Images/GraphView.png">'
							: '<i class="icon-graph"></i>',
						click: (e) => {
							this.goToSecurityPrice(this.getSelectedSecNumber(e));
						},
					},
				],
			},
			/* , {
            width: 55,
            command: [
                {
                    name: "goToSecurityAdministration",
                    text: '<img src="Images/goout.gif">',
                    click: (e) => { this.openDetails(this.getSelectedSecNumber(e)) }
                }
            ]
        } */
		);
		columnDefs.join();

		let portfGrid: kendo.ui.Grid = $("#" + this.gridName).data("kendoGrid");

		if (portfGrid == undefined) {
			let portfGridOptions: kendo.ui.GridOptions = {};
			portfGridOptions.dataSource = dataSource;
			portfGridOptions.height = "100%";
			portfGridOptions.sortable = true;
			portfGridOptions.filterable = {
				messages: {
					selectedItemsFormat: (() => {
						const res = this._localize.getLocalizedResource("Res569");
						if (!res) return "{0} stavki izabrano";

						return res.indexOf("{0}") !== -1 ? res : "{0} " + res;
					})(),
					checkAll:
						this._localize.getLocalizedResource("Res2919") || "Izaberi sve",
					info:
						this._localize.getLocalizedResource("Res2903") ||
						"Prikaži redove sa vrednošću koja:",
					filter: this._localize.getLocalizedResource("Res2902") || "Filtriraj",
					clear: this._localize.getLocalizedResource("Res2918") || "Očisti",
					and: this._localize.getLocalizedResource("Res2916") || "I",
					or: this._localize.getLocalizedResource("Res2917") || "Ili",
					selectValue:
						this._localize.getLocalizedResource("Res2920") ||
						"-Izaberi vrednost-",
				},
				operators: {
					string: {
						eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
						neq:
							this._localize.getLocalizedResource("Res2905") || "Nije jednako",
						isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
						isnotnull:
							this._localize.getLocalizedResource("Res2911") || "Nije NULL",
						startswith:
							this._localize.getLocalizedResource("Res2906") || "Počinje sa",
						contains:
							this._localize.getLocalizedResource("Res2907") || "Sadrži",
					},
					number: {
						eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
						gt: this._localize.getLocalizedResource("Res495") || "Veće od",
						lt: this._localize.getLocalizedResource("Res496") || "Manje od",
						neq:
							this._localize.getLocalizedResource("Res2905") || "Nije jednako",
						isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
						isnotnull:
							this._localize.getLocalizedResource("Res2911") || "Nije NULL",
					},
					date: {
						eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
						neq:
							this._localize.getLocalizedResource("Res2905") || "Nije jednako",
						isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
						isnotnull:
							this._localize.getLocalizedResource("Res2911") || "Nije NULL",
					},
					enums: {
						eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
						neq:
							this._localize.getLocalizedResource("Res2905") || "Nije jednako",
						isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
						isnotnull:
							this._localize.getLocalizedResource("Res2911") || "Nije NULL",
					},
				},
			};
			portfGridOptions.columnMenu = {
				columns: false,
				messages: {
					sortAscending:
						this._localize.getLocalizedResource("Res2900") ||
						"Sortiraj rastuće",
					sortDescending:
						this._localize.getLocalizedResource("Res2901") ||
						"Sortiraj opadajuće",
					filter: this._localize.getLocalizedResource("Res2902") || "Filter",
				},
			};

			portfGridOptions.toolbar = [
				"excel",
				"pdf",
				{
					name: "gridWizard",
					text: "Grid Wizard",
					template:
						"<button type='button' id='custom-grid-wizard-toggle' class='k-button k-button-icontext'><span class='k-icon custom-grid-wizard'></span>Grid Wizard</button>",
				},
			];
			portfGridOptions.excel = {
				fileName: "Portfolio.xlsx",
				filterable: true,
				allPages: true,
			};
			portfGridOptions.excelExport = function (e) {
				let sheet = e.workbook.sheets[0];
				for (let i = 0; i < sheet.rows.length; i++) {
					if (
						sheet.rows[i]["type"] === "group-header" ||
						sheet.rows[i]["type"] === "group-footer" ||
						sheet.rows[i]["type"] === "footer"
					) {
						for (var ci = 0; ci < sheet.rows[i].cells.length; ci++) {
							if (sheet.rows[i].cells[ci].value !== undefined)
								sheet.rows[i].cells[ci].value = $(
									$.parseHTML(sheet.rows[i].cells[ci].value.toString()),
								)[0].innerText;
						}
					}
				}
			};
			portfGridOptions.selectable = true;
			portfGridOptions.reorderable = true;
			portfGridOptions.scrollable = true;
			portfGridOptions.noRecords = {
				template: SharedMethodsModule.noDataTemplate(),
			};
			portfGridOptions.pageable = false;
			if (window.innerWidth > 768) {
				portfGridOptions.filterMenuInit =
					CustomGridFilterModule.onFilterMenuInit;
			}
			if (this.enableLockedColumnOnPortfolioGrid) {
				portfGridOptions.groupable = false;
			} else {
				portfGridOptions.groupable = {
					messages: {
						empty:
							this._localize.getLocalizedResource("Res2921") ||
							"Prevucite zaglavlje kolone i spustite ga ovde da biste grupisali po toj koloni",
					},
				};
			}
			portfGridOptions.group = function (e) {
				if (e.groups !== undefined && e.groups !== null) {
					let lastSavedGrouping = e.groups;
					localStorage.setItem(
						"portfolioGridGrouping",
						JSON.stringify(lastSavedGrouping),
					);
				}
			};
			portfGridOptions.sort = function (e) {
				if (e.sort !== undefined && e.sort !== null) {
					let lastSavedPortfSorting = e.sort;
					localStorage.setItem(
						"portfolioGridSorting",
						JSON.stringify(lastSavedPortfSorting),
					);
				}
			};
			portfGridOptions.resizable = true;
			portfGridOptions.columns = columnDefs;
			portfGridOptions.columnReorder = customGroupHeader.gridColumnReorder;
			portfGridOptions.dataBound = customGroupHeader.KendoGridDataBound;
			SharedMethodsModule.saveGridOptionsOnChange(
				portfGridOptions,
				this.gridName,
			);
			const savedOptions = SharedMethodsModule.getSavedGridOptions(
				columnDefs,
				this.gridName,
			);
			const combinedGridOptions = SharedMethodsModule.combineGridInitOptions(
				savedOptions,
				portfGridOptions,
			);
			SharedMethodsModule.fitColumnsToHeaderTitles(combinedGridOptions.columns);
			$("#" + this.gridName).kendoGrid(combinedGridOptions);
			portfGrid = $("#" + this.gridName).data("kendoGrid");
			$("#" + this.gridName).on("click", ".excelExportBtn", () => {
				this.exportToExcel();
			});

			setTimeout(() => {
				$("#custom-grid-wizard-toggle").off("click.gridWizard");
				$("#custom-grid-wizard-toggle").on("click.gridWizard", (e) => {
					e.preventDefault();
					this._zone.run(() => this.openGridWizard());
				});
			}, 10);
			if (savedOptions && savedOptions.dataSource) {
				portfGrid.dataSource.group(savedOptions.dataSource.group);
				portfGrid.dataSource.sort(savedOptions.dataSource.sort);
			}
		} else {
			portfGrid.setDataSource(dataSource);
			//$("#" + this.gridName).kendoGrid.setDataSource(dataSource);
		}

		portfGrid.thead
			.find("[data-field=SecNumber]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=Quantity]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=CurrentPrice]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=Valuation]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=PurchasePrice]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=PurchaseValue]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=UnrealisedProfit]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=ProfitPerc]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=AccruedInterest]>.k-header-column-menu")
			.remove();
		portfGrid.thead
			.find("[data-field=PercentOfTotal]>.k-header-column-menu")
			.remove();
		portfGrid.thead.find("[data-field=Yield]>.k-header-column-menu").remove();
		//portfGrid.thead.find("[data-field=InterestFrequency]>.k-header-column-menu").remove();

		// SharedMethodsModule.loadGridGroupingAndSorting("PortfolioGrid");
		portfGrid.options.height = $(".gridResize").height();
		portfGrid.resize();
	}

	public getSelectedSecNumber(e) {
		e.preventDefault();
		let portfGrid: kendo.ui.Grid = $("#" + this.gridName).data("kendoGrid");
		let tr = $(e.target).closest("tr");
		let data = portfGrid.dataItem(tr);
		let secNumber: string;
		if (data["SecMainClass"] === appConfig.SecMainClass_LIQUIDITY) {
			if (this._insaService.allPortfoliosNoLiquidity.length > 0) {
				secNumber = this._insaService.allPortfoliosNoLiquidity
					.slice(0)
					.sort(function (a, b) {
						return b.Valuation - a.Valuation;
					})[0].SecNumber;
			}
		} else {
			secNumber = data.get("SecNumber");
		}
		return secNumber;
	}

	openGridWizard() {
		this.isGridWizardVisible = true;
	}

	closeGridWizard() {
		this.isGridWizardVisible = false;
	}

	applyGridSettings(newOptions) {
		// Sprečavamo višestruke klikove
		if (this.isApplyingGridSettings) {
			return;
		}

		this.isApplyingGridSettings = true;
		kendo.ui.progress($(".portfGrid-loading"), true);

		const kendoGrid = $("#" + this.gridName).data("kendoGrid");
		if (kendoGrid) {
			setTimeout(() => {
				try {
					kendoGrid.setOptions(newOptions);
					setTimeout(() => {
						$("#custom-grid-wizard-toggle").off("click.gridWizard");
						$("#custom-grid-wizard-toggle").on("click.gridWizard", (e) => {
							e.preventDefault();
							this._zone.run(() => this.openGridWizard());
						});
					}, 100);
				} finally {
					kendo.ui.progress($(".portfGrid-loading"), false);
					this.isApplyingGridSettings = false;
					this.closeGridWizard();
					// Omogućavamo ponovno otvaranje modal-a
					this.isGridWizardVisible = false;
				}
			}, 100);
		} else {
			kendo.ui.progress($(".portfGrid-loading"), false);
			this.isApplyingGridSettings = false;
			this.closeGridWizard();
		}
	}
	ngAfterViewInit() {
		kendo.ui.progress($(".portfGrid-loading"), true);

		if (this._insaService.portfolios) {
			setTimeout(() => {
				this.refreshPortfolios(this._insaService.portfolios);
			}, 100);
		} else if (!this._appStateProvider.fetchingPortfolios) {
			kendo.ui.progress($(".portfGrid-loading"), false);
		}
	}

	ngOnDestroy() {
		this.portfolioSubscription.unsubscribe();
	}
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-grids/portfolio.component.ts